'use client';
import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import { Image } from '@ahafood/ui/components/image';
import { Separator } from '@ahafood/ui/components/separator';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn, JSFunction } from '@ahm/common-helpers';
import { getCookie } from 'cookies-next';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { useParams, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import {
  PersonalizeItemCard,
  PersonalizeItemSkeleton,
} from './for-you-item-list';
import SolomealHeader from './solomeal-header';
import { useGetPersonalize } from '@/api/use-get-personalize';
import NotificationDemo from '@/app/[locale]/(main)/store/components/store-notification';
import UserButton from '@/components/user/user-button';
import { useStoreCitySheet } from '@/contexts/store-city-sheet-context';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { usePathname } from '@/i18n/routing';
import MainHeader from '@/layouts/main/main-header';
import { clearDebugLogs } from '@/lib/debug/use-debug-tracker';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile';
import { useDetectWebInApp } from '@/lib/is-webinapp/use-detect-webinapp';
import { useAuth } from '@/providers/auth-provider';
import { CITIES, useAiFoodOrderStore, useAuthStore } from '@/store';

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function StoreHeader() {
  const params = useParams();
  const searchParams = useSearchParams();
  const latParam = searchParams.get('lat');
  const lngParam = searchParams.get('lng');
  const address = useAiFoodOrderStore.use.address();
  const citySlug = Array.isArray(params.city) ? params.city[0] : params.city;
  const cityData = CITIES.find((c) => c.slug === citySlug);
  const cityLabel = cityData?.label ?? citySlug;
  const cityId = cityData?.value ?? 'SGN';
  const routes = useCityRoutes();
  const pathName = usePathname();
  const isMobile = useIsMobile();
  const isWebInApp = useDetectWebInApp();

  const addressCookie = getCookie('ahafood_address');
  const isHomepage = pathName === routes.home;
  const { isLoading } = useAuth();

  // Use context for centralized sheet management
  const { openCitySheet, isNavigating } = useStoreCitySheet();
  const { user } = useAuthStore();
  const personalParams = {
    customer_id: user?.cunstomerId ?? '',
    city_id: cityId ?? 'SGN',
    lat: address?.lat ?? 10.7769,
    lng: address?.lng ?? 106.7009,
    limit: 10,
  };

  const { data: personalizeData, isLoading: isPersonalizeLoading } =
    useGetPersonalize(personalParams, {
      enabled: !!user?.cunstomerId && !!cityId,
      retry: 1,
    });

  const logBackButtonClick = () => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'is_back',
      button_location: 'home',
      timestamp: Date.now(),
      user_id: user?.cunstomerId,
    });
  };

  const handleToggleAmori = () => {
    if (
      typeof window !== 'undefined' &&
      typeof window.setOpenChange === 'function'
    ) {
      window.setOpenChange(true);
      trackEvent('chatbot_event', {
        action_time: Math.floor(Date.now() / 1000),
        button_name: 'chatbot_banner',
        timestamp: Date.now(),
        user_id: user?.cunstomerId,
      });
    }
  };

  useEffect(() => {
    // Only open city sheet if no address in cookie/store AND no lat/lng in URL AND not loading
    // URL params have highest priority and are handled in auth-provider
    if (
      !addressCookie &&
      !address &&
      !isNavigating &&
      !isLoading && // Don't open sheet while auth-provider is loading
      !latParam &&
      !lngParam
    ) {
      console.log('🏠 Opening city sheet - no address and no URL params');
      openCitySheet();
    } else if (isLoading && (latParam ?? lngParam)) {
      console.log('🏠 Skipping city sheet - URL params being processed');
    }
  }, [
    addressCookie,
    address,
    isNavigating,
    isLoading,
    openCitySheet,
    latParam,
    lngParam,
  ]);

  // Preload critical header background image for LCP optimization
  useEffect(() => {
    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.as = 'image';
    preloadLink.href = `${assetPrefix}/static/images/mb-header-bg.webp`;
    preloadLink.type = 'image/webp';
    document.head.appendChild(preloadLink);

    return () => {
      // Cleanup on unmount
      if (document.head.contains(preloadLink)) {
        document.head.removeChild(preloadLink);
      }
    };
  }, []);

  // Note: Removed city mismatch logic as it was causing duplicate sheet opening
  // The city change is now handled entirely within StoreCityIdSheet component

  if (pathName.includes('/danh-sach-cua-hang')) {
    // On store listing pages, the sheet is managed centrally by context
    return null;
  }

  // Check if this is a solomeal page
  const isSolomealPage =
    pathName.includes('/solomeal') && !pathName.includes('/danh-sach-cua-hang');

  // Handle solomeal pages first (they manage their own WebInApp header)
  if (isSolomealPage) {
    return <SolomealHeader />;
  }

  // Desktop version
  if (!isMobile) {
    return <MainHeader />;
  }

  // Mobile version (unified for both web and WebInApp)
  return (
    <>
      <NotificationDemo />
      <div className="relative overflow-hidden px-4 pb-6 flex flex-col overflow-x-clip">
        <Image
          src={`${assetPrefix}/static/icons/header-mb-background.svg`}
          alt="Header Background"
          fill
          className="object-cover"
          priority
        />

        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              'linear-gradient(120deg, rgba(255, 127, 50, 0.5) 0%, rgba(255, 255, 255, 0) 52.88%, rgba(234, 63, 237, 0.2) 79.33%, rgba(254, 188, 47, 0.2) 90.38%)',
          }}
        />

        <header
          className={cn(
            'mx-auto flex flex-col h-auto w-full max-w-screen-xl md:gap-4'
          )}
        >
          <div className="py-6">
            <Card
              className={cn(
                'w-full rounded-full bg-white/60 p-2 backdrop-blur-sm cursor-pointer text-left flex gap-4 items-center border border-white transition-colors',
                isWebInApp ? 'justify-start' : 'justify-between pl-4'
              )}
              onClick={() => openCitySheet()}
            >
              {isWebInApp && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    logBackButtonClick();
                    clearDebugLogs();
                    JSFunction.call({ name: 'close' });
                  }}
                  className="flex size-10 items-center justify-center rounded-full bg-white/80 backdrop-blur-sm"
                >
                  <ArrowLeft className="size-6 text-neutral-70" />
                </button>
              )}
              <div className="flex flex-col gap-0 hover:bg-none bg-transparent text-left px-0">
                <span className="text-neutral-90 text-sm font-bold flex items-center">
                  <Image
                    width={16}
                    height={16}
                    className="mr-1"
                    src={`${assetPrefix}/static/icons/location.svg`}
                    alt={'Location Icon'}
                  />
                  Giao món đến
                  <ChevronDown className="ml-2 size-3 text-primary" />
                </span>
                <div className="flex items-center">
                  <span className="text-primary text-xs font-semibold truncate max-w-[calc(100vw-120px)]">
                    {address?.address ?? cityLabel}
                  </span>
                </div>
              </div>
              <div
                role="button"
                className="flex items-center"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                  }
                }}
                tabIndex={0}
              >
                {!isWebInApp ? <UserButton /> : null}
              </div>
            </Card>
          </div>

          <div
            className={
              'px-4 flex flex-col gap-0 bg-gradient-to-r from-[#0D4073] via-[#FD74FF] to-[#FF7F32] bg-clip-text text-transparent mb-2'
            }
          >
            <span className="text-3xl font-bold">
              Gợi ý từ Amori <span className="text-neutral-15">🤍</span>
            </span>
            <span className="text-sm font-thin">
              AI giúp bạn đỡ phải nghĩ, gọi món nhanh trong 10s
            </span>
          </div>

          {/* Personalize Items or AI Chat Card */}
          {isPersonalizeLoading ? (
            <div className="w-screen overflow-x-scroll scrollbar-hide mt-3 -ml-4">
              <div className="flex gap-3 pb-2 px-4">
                <div className="shrink-0 w-[calc(100vw-40px)]">
                  <PersonalizeItemSkeleton />
                </div>
                <div className="shrink-0 w-[calc(100vw-40px)]">
                  <PersonalizeItemSkeleton />
                </div>
              </div>
            </div>
          ) : personalizeData?.items &&
            personalizeData.debug.is_new_user === false &&
            personalizeData.items.length > 0 &&
            isHomepage ? (
            <div className="w-screen overflow-x-scroll scrollbar-hide mt-3 -ml-4">
              <div className="flex gap-3 pb-2 px-4">
                {personalizeData.items.map((item) => (
                  <div key={item._id} className="shrink-0 w-[calc(100vw-40px)]">
                    <PersonalizeItemCard
                      location="amori_home_zone"
                      item={item}
                      isAuthenticated={!!user?.cunstomerId}
                      // eslint-disable-next-line @typescript-eslint/no-empty-function
                      setShowLoginDialog={() => {}}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <Card
              onClick={(e) => {
                e.stopPropagation();
                handleToggleAmori();
              }}
              className=" flex flex-col gap-0 bg-white/60 px-4 pb-4 mt-8 rounded-3xl pt-6 backdrop-blur-sm cursor-pointer text-left items-center border border-white transition-colors relative"
            >
              <span className="text-lg font-medium w-full text-left">
                Chat với{' '}
                <span className="bg-gradient-to-r from-[#0D4073] via-[#FD74FF] to-[#FF7F32] bg-clip-text text-transparent font-bold">
                  Amori
                </span>{' '}
                tại đây...
              </span>
              <div className="flex gap-3 w-full items-center">
                <div className="flex flex-col gap-1 flex-1">
                  <Separator
                    orientation="vertical"
                    className="h-0.5 bg-gradient-to-r from-[#3342FF00] via-[#37F4F4] to-[#3342FF] w-full"
                  />
                  <span className="font-normal text-xs">
                    Người bạn AI thiết kế bữa ngon cho bạn
                  </span>
                </div>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleAmori();
                  }}
                  className="relative px-6 py-2.5 font-bold text-white rounded-2xl overflow-hidden flex items-center gap-2 bg-gradient-to-b from-primary to-primary-30"
                >
                  {/* Inner shadows */}
                  <div
                    className="absolute inset-0 rounded-2xl pointer-events-none"
                    style={{
                      boxShadow:
                        'inset 0px 0px 5px 0px #FFACE4, inset 0px 0px 5px 0px #FFACE4',
                    }}
                  />
                  {/* Overlay blend */}
                  <div
                    className="absolute inset-0 rounded-2xl pointer-events-none"
                    style={{
                      background: 'rgba(254, 200, 241, 0)',
                      mixBlendMode: 'overlay',
                    }}
                  />
                  {/* Star Icon */}
                  <Image
                    src={`${assetPrefix}/static/icons/star.svg`}
                    alt="AI Icon"
                    width={16}
                    height={16}
                  />
                  {/* Text */}
                  Hỏi AI
                </Button>
              </div>
              <div
                className="absolute right-0 -top-10 size-28 flex items-center justify-center"
                style={{
                  backgroundImage:
                    'url(/static/icons/amori-avt-header-case.svg)',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'contain',
                }}
              >
                <div className="size-16 relative mb-5">
                  <Image
                    src={`${assetPrefix}/static/images/amori-avt.webp`}
                    fill
                    alt={'amori'}
                  />
                </div>
              </div>
            </Card>
          )}
        </header>
      </div>
    </>
  );
}
